import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Megaphone,
  Plus,
  Search,
  Pin,
  Calendar,
  User,
  Eye,
  Trash2,
  Edit,
  AlertCircle,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CreateAnnouncementModal } from '@/components/CreateAnnouncementModal'
import { AnnouncementModel } from '@/models/announcement.model'
import { UserRole } from '@/models/user.model'
import { useAuth } from '@/contexts/AuthContext'
import {
  useAnnouncements,
  useAnnouncementPinState,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from '@/hooks/useAnnouncements'
import { Paginator } from '@/components/ui/Paginator'
import { useCourses } from '@/hooks/useCourses'
import { useProgrammes } from '@/hooks/useProgrammes'
import { paths } from '@/routes/path'
import { AnnouncementMutationPayload } from '@/services/announcementsService'

type CourseFilter = 'all' | 'general' | 'course'

export function AnnouncementsPage() {
  const { user } = useAuth()
  const canManage = user?.role === UserRole.ADMIN || user?.role === UserRole.INSTRUCTOR

  const [pagination, setPagination] = useState({ page: 1, perPage: 10 })
  const [searchInput, setSearchInput] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [courseFilter, setCourseFilter] = useState<CourseFilter>('all')
  const [showPinnedOnly, setShowPinnedOnly] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<AnnouncementModel | null>(null)
  const [actioningId, setActioningId] = useState<string | null>(null)

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(searchInput.trim())
      setPagination((prev) => ({ ...prev, page: 1 }))
    }, 400)
    return () => clearTimeout(handler)
  }, [searchInput])

  const { data, isLoading, isFetching, error } = useAnnouncements({
    page: pagination.page,
    perPage: pagination.perPage,
    search: searchValue || undefined,
  })

  const announcements: AnnouncementModel[] = data?.data ?? []

  const filteredAnnouncements = useMemo(() => {
    let next = announcements
    if (courseFilter === 'general') {
      next = next.filter((announcement) => !announcement.courseId)
    } else if (courseFilter === 'course') {
      next = next.filter((announcement) => Boolean(announcement.courseId))
    }
    if (showPinnedOnly) {
      next = next.filter((announcement) => announcement.isPinned)
    }
    return next
  }, [announcements, courseFilter, showPinnedOnly])

  const pinnedAnnouncements = filteredAnnouncements.filter((announcement) => announcement.isPinned)
  const nonPinnedAnnouncements = filteredAnnouncements.filter((announcement) => !announcement.isPinned)
  const showPinnedSection = pinnedAnnouncements.length > 0 && !showPinnedOnly
  const listForAllSection = showPinnedOnly ? pinnedAnnouncements : nonPinnedAnnouncements

  const createAnnouncement = useCreateAnnouncement()
  const updateAnnouncement = useUpdateAnnouncement()
  const deleteAnnouncement = useDeleteAnnouncement()
  const pinAnnouncement = useAnnouncementPinState()

  const { data: courseResponse } = useCourses({ limit: 50 })
  const { programmes } = useProgrammes()

  const courseOptions =
    courseResponse?.data.map((course) => ({
      id: course.id,
      title: course.title ?? course.code ?? 'Untitled course',
      programmeId: course.programmeId ?? null,
    })) ?? []

  const programmeOptions = programmes.map((programme) => ({
    id: programme.id ?? '',
    title: programme.title ?? 'Untitled programme',
  })).filter((programme) => programme.id)

  const openCreateModal = () => {
    setEditingAnnouncement(null)
    setIsModalOpen(true)
  }

  const handleEdit = (announcement: AnnouncementModel) => {
    setEditingAnnouncement(announcement)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingAnnouncement(null)
  }

  const handleSubmitAnnouncement = async (payload: AnnouncementMutationPayload, announcementId?: string | null) => {
    try {
      if (announcementId) {
        await updateAnnouncement.mutateAsync({ announcementId, payload })
        toast.success('Announcement updated')
      } else {
        await createAnnouncement.mutateAsync(payload)
        toast.success('Announcement created')
      }
      handleModalClose()
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to save announcement')
    }
  }

  const handleDelete = async (announcement: AnnouncementModel) => {
    if (!announcement.id) return
    const confirmed = window.confirm(`Delete announcement “${announcement.title}”?`)
    if (!confirmed) return
    try {
      setActioningId(announcement.id)
      await deleteAnnouncement.mutateAsync(announcement.id)
      toast.success('Announcement deleted')
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to delete announcement')
    } finally {
      setActioningId(null)
    }
  }

  const handlePinToggle = async (announcement: AnnouncementModel) => {
    if (!announcement.id) return
    try {
      setActioningId(announcement.id)
      await pinAnnouncement.mutateAsync({
        announcementId: announcement.id,
        action: announcement.isPinned ? 'unpin' : 'pin',
      })
      toast.success(announcement.isPinned ? 'Announcement unpinned' : 'Announcement pinned')
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to update pin state')
    } finally {
      setActioningId(null)
    }
  }

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1) return
    setPagination((prev) => ({ ...prev, page: nextPage }))
  }

  const handlePerPageChange = (perPage: number) => {
    setPagination({ page: 1, perPage })
  }

  const isSaving = createAnnouncement.isPending || updateAnnouncement.isPending
  const busyAnnouncementId = actioningId && (pinAnnouncement.isPending || deleteAnnouncement.isPending) ? actioningId : null

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Announcements</h1>
          <p className="text-secondary-600">Stay on top of important updates for every learner.</p>
        </div>
        {canManage && (
          <Button onClick={openCreateModal}>
            <Plus className="mr-2 h-4 w-4" />
            New announcement
          </Button>
        )}
      </header>

      <section className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
            <Input
              placeholder="Search announcements…"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={courseFilter}
            onChange={(event) => {
              setCourseFilter(event.target.value as CourseFilter)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All announcements</option>
            <option value="general">General</option>
            <option value="course">Course specific</option>
          </select>
          <Button
            type="button"
            variant={showPinnedOnly ? 'default' : 'outline'}
            onClick={() => setShowPinnedOnly((prev) => !prev)}
          >
            <Pin className="mr-2 h-4 w-4" />
            Pinned only
          </Button>
        </div>
      </section>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-center gap-3 py-4 text-sm text-red-700">
            <AlertCircle className="h-5 w-5" />
            {(error as Error).message || 'Failed to load announcements'}
          </CardContent>
        </Card>
      )}

      {showPinnedSection && (
        <section className="space-y-4">
          <h2 className="flex items-center text-lg font-semibold text-secondary-900">
            <Pin className="mr-2 h-4 w-4 text-warning-600" />
            Pinned announcements
          </h2>
          <div className="space-y-4">
            {pinnedAnnouncements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                canManage={canManage}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePin={handlePinToggle}
                isBusy={busyAnnouncementId === announcement.id}
              />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-secondary-900">{showPinnedOnly ? 'Pinned announcements' : 'All announcements'}</h2>
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 w-1/2 rounded bg-secondary-200" />
                  <div className="mt-3 h-3 w-1/3 rounded bg-secondary-100" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 rounded bg-secondary-100" />
                    <div className="h-3 rounded bg-secondary-100" />
                    <div className="h-3 w-2/3 rounded bg-secondary-100" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-secondary-500">
              <Megaphone className="h-10 w-10 text-secondary-300" />
              <p>No announcements match your filters.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {listForAllSection.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                canManage={canManage}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePin={handlePinToggle}
                isBusy={busyAnnouncementId === announcement.id}
              />
            ))}
          </div>
        )}
      </section>

      {filteredAnnouncements.length > 0 && (
        <Paginator
          meta={data?.meta}
          page={pagination.page}
          perPage={pagination.perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          isPreviousData={isFetching}
        />
      )}

      <CreateAnnouncementModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmitAnnouncement}
        isSubmitting={isSaving}
        announcement={editingAnnouncement ?? undefined}
        courses={courseOptions}
        programmes={programmeOptions}
      />
    </div>
  )
}

interface AnnouncementCardProps {
  announcement: AnnouncementModel
  canManage: boolean
  onEdit: (announcement: AnnouncementModel) => void
  onDelete: (announcement: AnnouncementModel) => void
  onTogglePin: (announcement: AnnouncementModel) => void
  isBusy?: boolean
}

function AnnouncementCard({ announcement, canManage, onEdit, onDelete, onTogglePin, isBusy }: AnnouncementCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTimeAgo = (dateString?: string | null) => {
    if (!dateString) return ''
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    if (diffMinutes < 1) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(dateString)
  }

  return (
    <Card className={`transition hover:shadow-md ${announcement.isPinned ? 'border-warning-200 bg-warning-50' : ''}`}>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              {announcement.isPinned && <Pin className="h-4 w-4 text-warning-600" />}
              <CardTitle className="text-xl text-secondary-900">{announcement.title}</CardTitle>
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-secondary-500">
              {announcement.authorName && (
                <span className="inline-flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {announcement.authorName}
                </span>
              )}
              {announcement.createdAt && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {getTimeAgo(announcement.createdAt)}
                </span>
              )}
              {announcement.courseTitle && <Badge variant="outline">{announcement.courseTitle}</Badge>}
            </div>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2 text-sm">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onTogglePin(announcement)}
                disabled={isBusy}
              >
                <Pin className="mr-1 h-3 w-3" />
                {announcement.isPinned ? 'Unpin' : 'Pin'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(announcement)} disabled={isBusy}>
                <Edit className="mr-1 h-3 w-3" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => onDelete(announcement)}
                disabled={isBusy}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            </div>
          )}
        </div>
        {announcement.programmeTitle && (
          <CardDescription>Programme: {announcement.programmeTitle}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-secondary-700">
          {isExpanded ? (
            <p className="whitespace-pre-wrap">{announcement.content}</p>
          ) : (
            <p className="line-clamp-3">{announcement.content}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded((prev) => !prev)}>
              {isExpanded ? 'Show less' : 'Read more'}
            </Button>
            <Link to={paths.app.announcements.detail.build(announcement.id)}>
              <Button variant="ghost" size="sm">
                <Eye className="mr-1 h-4 w-4" />
                View details
              </Button>
            </Link>
          </div>
          {announcement.createdAt && (
            <p className="text-xs text-secondary-500">Published {formatDate(announcement.createdAt)}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
